/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { SideResponse } from "../../../codegen-api";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { getAssetShortName } from "../../../utils/asset/assets";
import { Chevron } from "../../shared/Chevron/style";
import Dropdown from "../../shared/Dropdown";
import { Input } from "../../shared/Input";
import TickerLogo from "../../shared/TickerLogo";
import { InputWrapper, WarningLabel } from "../style";
import { sanitizedRegister } from "./sanitizedRegister";
import { DropdownItem, DropdownTitle } from "./style";
import { IOrderSizeInputProps, PerpFormFieldKeyEnum } from "./form.types";

export function OrderSizeInput({
  register,
  errors,
  warning,
  style,
  customError,
  selectedAsset,
  onChangeAsset,
  minAmountSize,
  existingPositionSize,
  existingOrderInfo,
  underlyingAsset,
  placeholder,
  showValidationError = true,
}: IOrderSizeInputProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.Form",
  });
  const { t: formError } = useTranslation("formErrors", {
    keyPrefix: "orderSize",
  });

  const [show, setShow] = useState(false);
  // Unique per instance: the trade form and the edit-order modal can both be
  // mounted, and both register a field called "amount".
  const errorId = `order-size-error-${useId().replace(/:/g, "")}`;
  const error = errors?.[PerpFormFieldKeyEnum.AMOUNT];
  const hasInputError = showValidationError && Boolean(error?.type);
  const hasError = hasInputError || Boolean(customError);

  return (
    <InputWrapper style={style}>
      <Input
        error={hasError}
        inputMode="decimal"
        placeholder={placeholder || "0"}
        // Raw `{...register}` let the field hold anything the DOM would: "1e3"
        // sized a $1,000 order with the CTA enabled, "-5" rendered an Order
        // Value of -$5.00, and "abc" / "1.2.3" sat there as typed.
        {...sanitizedRegister(register)}
        // After the spread: react-hook-form supplies name/ref/handlers only, but
        // spreading last would silently win any future collision. The field was
        // in a visibly red error state with nothing exposed to assistive tech.
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        leftAccessory={
          <span style={{
            fontSize: "11px",
            color: TEXT_COLORS.three,
            whiteSpace: "nowrap",
            paddingLeft: 4,
            paddingRight: 8,
            marginRight: 4,
            flexShrink: 0,
          }}>
            {t("order_size")}
          </span>
        }
        inputStyles={{ textAlign: "right", paddingLeft: 0 }}
        rightAccessory={
          <Dropdown
            toggleRole="combobox"
            toggleLabel="Order size unit"
            onToggle={(open) => setShow(open)}
            toggleStyles={{
              borderRadius: 100,
              padding: `0 ${SPACING.one}px`,
              background: "transparent",
            }}
            dropdownMenuContainerStyles={{
              overflow: "hidden",
              borderRadius: "8px",
            }}
            title={
              <DropdownTitle>
                <span style={{ marginRight: SPACING.one, fontWeight: 500 }}>
                  {selectedAsset
                    ? (getAssetShortName(selectedAsset) || selectedAsset)
                    : "USDC"}
                </span>
                <Chevron direction={show ? "up" : "down"} />
              </DropdownTitle>
            }
            items={[
              {
                label: (
                  <DropdownItem isSelected={!selectedAsset}>
                    <TickerLogo asset="USDC" size={24} />
                    USDC
                  </DropdownItem>
                ),
                onSelect: () => onChangeAsset(undefined),
              },
              ...(underlyingAsset
                ? [
                    {
                      label: (
                        <DropdownItem isSelected={!!selectedAsset}>
                          <TickerLogo asset={underlyingAsset} size={24} />
                          {getAssetShortName(underlyingAsset) || underlyingAsset}
                        </DropdownItem>
                      ),
                      onSelect: () => onChangeAsset(underlyingAsset),
                    },
                  ]
                : []),
            ]}
          />
        }
      />
      {/* Always rendered, empty or not: a live region added to the DOM at the
          moment it has something to say is announced unreliably. Same reasoning
          and same shape as NumericField/FieldError.tsx. */}
      <span id={errorId} role="alert" aria-live="polite">
      {hasInputError && error?.type === FormValidatorKeysEnum.required && (
        <WarningLabel>{formError("required")}</WarningLabel>
      )}
      {hasInputError && error?.type === FormValidatorKeysEnum.notEnoughBalance && (
        <WarningLabel>{formError("insufficient_buying_power")}</WarningLabel>
      )}
      {hasInputError &&
        error?.type === FormValidatorKeysEnum.reduceOnlyOrderSizeValid && (
        <WarningLabel>
          {formError("reduce_size_invalid", { size: existingPositionSize })}
        </WarningLabel>
      )}
      {hasInputError &&
        error?.type ===
          FormValidatorKeysEnum.reduceOnlyOrderSizeValidWithExistingOrder && (
        <WarningLabel>
          {existingOrderInfo?.side === SideResponse.Buy
            ? formError("reduce_size_invalid_existing_order_long", {
                newOrderSize: existingOrderInfo?.newOrderSizeLimit,
                existingOrderSize: existingOrderInfo?.size,
                positionSize: existingPositionSize,
              })
            : formError("reduce_size_invalid_existing_order_short", {
                newOrderSize: existingOrderInfo?.newOrderSizeLimit,
                existingOrderSize: existingOrderInfo?.size,
                positionSize: existingPositionSize,
              })}
        </WarningLabel>
      )}
      {hasInputError && error?.type === FormValidatorKeysEnum.notANumber && (
        <WarningLabel>{formError("not_a_number")}</WarningLabel>
      )}
      {Boolean(
        hasInputError &&
          (error?.type === FormValidatorKeysEnum.moreThanZero ||
            error?.type === FormValidatorKeysEnum.decimalsTooSmall)
      ) && (
        <WarningLabel>
          {formError("min_order_size", { size: minAmountSize })}
        </WarningLabel>
      )}
      {Boolean(!hasInputError && customError) && (
        <WarningLabel>{customError}</WarningLabel>
      )}
      </span>
      {/* Advisory, not an error: deliberately outside the alert region and out
          of aria-describedby, so it is neither announced as a problem nor read
          as part of the field's description. */}
      {Boolean(!Object.keys(errors).length && warning) && (
        <WarningLabel $type="warning">{warning}</WarningLabel>
      )}
    </InputWrapper>
  );
}
